import PublicBotStatistic from "features/bots/PublicBots/PublicBotStatistic";
import React from "react";
import { toFixed } from "@shared/utils/numbers";
import useMeasure from "utils/hooks/useMeasure";
import styled, { useTheme } from "styled-components";
import { BotTemplateData, LanguageNameSpaces } from "@shared/types";
import pluralize from "pluralize";
import { getDaysFromDate } from "@shared/utils/date";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface PublicBotStatisticsProps {
  botData: BotTemplateData;
  created?: number;
  showBorder?: boolean;
  isBotForm?: boolean;
}

const PublicBotStatistics = ({
  botData,
  created,
  showBorder,
  isBotForm,
}: PublicBotStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const { isNewLook } = useTheme();

  const getChangeText = (change: number) => {
    return `${change > 0 ? "+" : ""}${toFixed(change * 100, 2)}%`;
  };

  const showDaysRunning = created && width > 550;
  const daysRunning = getDaysFromDate(created, true);

  return (
    <ChangeAndChartWrapper ref={measureRef} $isBotForm={isBotForm}>
      <PublicBotStatistic
        statisticKey={
          isMobile || width <= 380
            ? t("public_bots.last_90_days")
            : t("statistics.profit_last_90_days")
        }
        statisticValue={getChangeText(botData.last_90_days_change)}
        showBorder={showBorder}
        isNegative={botData.last_90_days_change < 0}
      />
      {!isBotForm && (
        <>
          <PublicBotStatistic
            statisticKey={
              isMobile || isNewLook
                ? t("public_bots.avg_monthly")
                : t("public_bots.avg_monthly_profit")
            }
            statisticValue={getChangeText(botData.average_monthly_profit)}
            showBorder={showBorder}
            style={
              showBorder && !showDaysRunning
                ? { paddingLeft: 8, paddingRight: 8 }
                : undefined
            }
            isNegative={botData.average_monthly_profit < 0}
          />
          <PublicBotStatistic
            statisticKey={t("public_bots.total_profit")}
            statisticValue={getChangeText(botData.change)}
            showBorder={showBorder}
            isNegative={botData.change < 0}
          />
          {showDaysRunning && (
            <PublicBotStatistic
              statisticKey={t("public_bots.days_running")}
              statisticValue={`${pluralize("day", daysRunning, true)}`}
              showBorder={showBorder}
            />
          )}
        </>
      )}
    </ChangeAndChartWrapper>
  );
};

export default PublicBotStatistics;

const ChangeAndChartWrapper = styled.div<{ $isBotForm?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-around;
  ${(p) =>
    !p.$isBotForm &&
    `
    width: 100%;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    !p.$isBotForm &&
    `
    align-items: stretch;
    gap: 1px;
    border: ${p.theme.borders.card};
    border-radius: 14px;
    background: ${p.theme.colors.cardBorderColor};
    overflow: hidden;
    > div {
      background: ${p.theme.colors.cardBgColor};
    }
  `};
  ${(p) =>
    p.theme.isNewLook &&
    p.$isBotForm &&
    `
    > div {
      padding: 0;
    }
  `};
`;
